import { useAgents } from '#~/agent/hooks.js';
import { fieldKeyedAgentsEqual, idNamePick } from '#~/agent/provider/use-agents-field-eq.js';
import { Lang } from '#~/audio/index.js';
import {
  Container,
  Filter,
  Header,
  Summary,
  Table
} from '#~/device/discovery/components/selector/index.js';
import { useDiscovery } from '#~/device/discovery/hooks.js';
import type { Discovery, Discovery$Audio } from '#~/device/discovery/index.js';
import { useMultiProxyDiscoveryStateQuery } from '#~/statequery/hook.js';
import {
  multiProxyDeviceDiscoveryStateQuery,
  transformMultiProxyDeviceDiscoveryToDiscovery
} from '#~/statequery/queries.js';
import { Icon } from '@studio/design-system/icon';
import type { HtmlAttributes } from '@studio/design-system/props';
import { vendorName } from '@studio/design-system/themes/vendor.js';
import { SortOrder } from '@studio/js-utils/sorting';
import { castId } from '@studio/models';
import type { Agent } from '@studio/models/agent';
import { toAccessor } from '@studio/react-utils';
import { useLatestFn, useLazyRef, useMount } from '@studio/react-utils/hooks';
import type { T_Maybe } from '@tryiris-ai/js-types';
import { toObjectEntries } from '@tryiris-ai/js-types';
import { pick } from 'lodash-es';
import type { ReactElement } from 'react';
import { useEffect, useMemo } from 'react';
import { shallow } from 'zustand/shallow';
import { useProducer, useStore } from '../store/hooks.js';
import { isDiscoveryInUse } from '../store/utils.js';
import { Filters } from './constants.js';
import { useFilterPref, useSortingPref } from './hooks.js';
import { Filter as T_Filter } from './types.js';

export const Selector = (props: HtmlAttributes): ReactElement => {
  const producer = useProducer();
  const discovery = useDiscovery(useStore((µ) => µ.agentId));
  const nonEmbeddedAgents = useAgents(
    (µ) => µ.filter((a) => !a.embedded).map((a) => pick(a, ['id', 'name'])),
    (a, b) => fieldKeyedAgentsEqual(a, b, idNamePick)
  );

  const { preferredId, selection, ...store } = useStore(
    (µ) => pick(µ.source, ['selection', 'preferredId', 'discoveries', 'existing']),
    shallow
  );

  const discoveriesRaw = useMultiProxyDiscoveryStateQuery(
    multiProxyDeviceDiscoveryStateQuery,
    2000
  );
  const agentId = useStore((µ) => µ.agentId);

  const discoveries = useMemo(() => {
    // @ts-ignore
    const transformed = transformMultiProxyDeviceDiscoveryToDiscovery(discoveriesRaw);
    const resultMap: Record<string, Discovery$Audio> = {};

    for (const discovery of Object.values(transformed)) {
      let { value } = discovery;
      if ('audio' in value) {
        if (value.agentIds?.includes(agentId)) {
          value = {
            ...value,
            id: (value.audio as any).path,
            agentId: castId<Agent['id']>(agentId)
          };
        }

        // @ts-ignore
        const key = value.audio?.path ?? value.audio?.host;

        // @ts-ignore
        const type = value.audio?.protocol;

        if (!key || !type) {
          continue;
        }

        // @ts-ignore
        resultMap[type + key] = value;
      }
    }

    const result = Object.values(resultMap);

    return result;
  }, [discoveriesRaw, agentId]);

  const [filter, setFilter] = useFilterPref();
  const [sorting, setSorting] = useSortingPref();

  const available = useLazyRef(() =>
    toObjectEntries(Filters).map(([k, v]) => [k, v.label] as [string, string])
  ).current;

  useMount(discovery.discover);

  const getAgentNames = (agents: Pick<Agent, 'id' | 'name'>[]) => {
    return agents.map((agent) => agent.name ?? 'Unknown');
  };

  const determineDiscoveryIp = (discovery: Discovery$Audio) => {
    // @ts-ignore
    return discovery.audio.host || 'N/A'; // For network audio; local audio may return 'N/A'
  };

  const tableData = useMemo(() => {
    const { key, order } = sorting;

    const valueOf = (µ: Discovery$Audio): string => {
      switch (key) {
        case 'name':
          return µ[key];
        case 'ip':
          return determineDiscoveryIp(µ);
        case 'bridge': {
          const canDiscoverSource = nonEmbeddedAgents.filter((agent) =>
            µ.agentIds?.includes(agent.id)
          );
          return canDiscoverSource.length > 0
            ? (getAgentNames(canDiscoverSource)[0] ?? 'Unknown')
            : 'Unknown';
        }
        // The 'brand' column surfaces fingerprint-matched vendor names.
        // Audio Configuration has no brand field, so read the optional
        // brand from the discovery itself (populated for fingerprint-
        // matched rows) and fall back to empty string.
        case 'brand':
          return (µ as { brand?: string }).brand ?? '';
        default:
          return µ.audio[key as 'protocol'] || '';
      }
    };

    return {
      discoveries: [...discoveries]
        .filter(
          (discovery) =>
            // Filter out discoveries that are already in use (deduplication)
            !store.existing.some((device) => isDiscoveryInUse(device, discovery))
        )
        .sort((a, b) => {
          const [x, y] = order === SortOrder.Ascending ? [a, b] : [b, a];
          const valueX = valueOf(x) ?? '';
          const valueY = valueOf(y) ?? '';
          return valueX.localeCompare(valueY);
        })
        .filter(Filters[filter].matches)
        .map((discovery) => {
          const canDiscoverSource = nonEmbeddedAgents.filter((agent) =>
            discovery.agentIds?.includes(agent.id)
          );
          return {
            ...pick(discovery, ['id', 'name']),
            connection: Lang.protocol[discovery.audio.protocol],
            ip: determineDiscoveryIp(discovery),
            bridge_s: canDiscoverSource.length > 0 ? getAgentNames(canDiscoverSource) : ['Unknown']
          };
        }),
      existing:
        filter === T_Filter.All
          ? [...store.existing].sort((a, b) => {
              const [x, y] = key === 'name' && order === SortOrder.Descending ? [b, a] : [a, b];
              return x.name.localeCompare(y.name);
            })
          : []
    };
  }, [filter, sorting, discoveries, store.existing, nonEmbeddedAgents]);

  const handleSelect = useLatestFn((nextId: T_Maybe<Discovery['id']>) => {
    producer(({ source }) => {
      source.preferredId = nextId;
    });
  });

  useEffect(() => {
    producer((state) => {
      state.source.selection = preferredId
        ? discoveries.find((µ) => µ.id === preferredId)
        : undefined;

      if (
        state.source.selection?.agentIds?.length &&
        !state.source.selection.agentIds.includes(state.agentId)
      ) {
        // Update agentId to match the selection’s agentId if current agentId isn’t valid
        state.agentId = state.source.selection.agentId ?? state.source.selection.agentIds[0]!;
      }
    });
  }, [discoveries, preferredId]);

  return (
    <Container {...props}>
      <Filter available={available} selection={toAccessor(filter, setFilter)} />
      <div role='table'>
        <Header
          className='border-y border-base-100'
          count={tableData.discoveries.length}
          sorting={toAccessor(sorting, setSorting)}
        />

        {/* UI to render if no agents are available/have connected yet */}
        {nonEmbeddedAgents.length === 0 && (
          <>
            <div className='m-16 flex items-center space-x-2 rounded border-1 border-error p-4 py-8 text-[12px]'>
              <Icon icon='alert-triangle text-error mr-4 text-[16px]' />
              <span>To discover Audio sources, you must first link an {vendorName()} Bridge.</span>
            </div>
            <hr />
          </>
        )}
        <Table
          className='h-[310px] overflow-y-auto overflow-x-hidden'
          discoveries={tableData.discoveries}
          existing={tableData.existing}
          selection={toAccessor(selection?.id, handleSelect)}
        />
      </div>
      <Summary
        className='border-t border-base-100'
        count={tableData.discoveries.length}
        discovery={discovery}
        qualifier={
          filter !== T_Filter.All ? `${Filters[filter].label.toLocaleLowerCase()}` : undefined
        }
      />
    </Container>
  );
};
